/**
 * Browser-only rendition production: canvas downscale to JPEG plus the Storage
 * upload. Pure path/size logic lives in `artworkRenditionHelpers.ts`.
 *
 * DECISION (JARVIS-122): renditions are produced CLIENT-SIDE on a canvas, not
 * by a Storage-triggered Cloud Function or the Resize Images extension. This
 * repo has no `functions/` workspace and no `functions` block in
 * `firebase.json`; it deploys to Heroku (`Procfile`: `web: node
 * server/dist/server.js`) and `.github/workflows/verify.yml` carries no
 * Firebase credentials, so nothing here could deploy or operate a function.
 * The Node server is not an option either: it has no image decoder without
 * adding `sharp`, a new top-level dependency. The browser already decodes the
 * image for the upload preview, so downscaling there is free.
 *
 * Orientation: `createImageBitmap` with `imageOrientation: "from-image"` bakes
 * EXIF orientation into the pixels, so the rendition displays upright without
 * relying on the browser honouring EXIF on a JPEG we re-encoded.
 * `artwork.rotation` is deliberately NOT baked in — it is applied at display
 * time, and the scanner and customer apps depend on that.
 */
import { map, zipObject } from "lodash"

import Firestore from "#classes/Firestore"
import {
  getRenditionPath,
  getScaledDimensions,
  RENDITION_LONG_EDGE,
  RENDITION_TIER,
  type RenditionTier,
} from "#utils/artworkRenditionHelpers"

import type { UploadMetadata } from "firebase/storage"

export const RENDITION_JPEG_QUALITY = 0.82
export const RENDITION_CONTENT_TYPE = "image/jpeg"

export type RenditionBlobs = Record<RenditionTier, Blob>
export type RenditionUrls = Record<RenditionTier, string>

const ALL_TIERS: RenditionTier[] = [RENDITION_TIER.THUMB, RENDITION_TIER.MED]

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error("Canvas produced no JPEG data for rendition"))
        }
      },
      RENDITION_CONTENT_TYPE,
      RENDITION_JPEG_QUALITY
    )
  })

const drawRendition = async (
  bitmap: ImageBitmap,
  tier: RenditionTier
): Promise<Blob> => {
  const { width, height } = getScaledDimensions(
    bitmap.width,
    bitmap.height,
    RENDITION_LONG_EDGE[tier]
  )
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("2D canvas context unavailable for rendition")
  }
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = "high"
  // Scans and PNGs can carry transparency; JPEG cannot, so flatten on white.
  context.fillStyle = "#ffffff"
  context.fillRect(0, 0, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  return canvasToJpeg(canvas)
}

const byTier = <T extends RenditionTier, V>(
  tiers: readonly T[],
  values: readonly V[]
): Record<T, V> => zipObject(tiers, values) as Record<T, V>

/** Decodes `source` once and draws only the requested tiers. */
export const createRenditionBlobsForTiers = async <T extends RenditionTier>(
  source: Blob,
  tiers: readonly T[]
): Promise<Record<T, Blob>> => {
  const bitmap = await createImageBitmap(source, {
    imageOrientation: "from-image",
  })
  try {
    const blobs = await Promise.all(
      map(tiers, (tier) => drawRendition(bitmap, tier))
    )
    return byTier(tiers, blobs)
  } finally {
    bitmap.close()
  }
}

export const createRenditionBlobs = (source: Blob): Promise<RenditionBlobs> =>
  createRenditionBlobsForTiers(source, ALL_TIERS)

/**
 * Writes only `tiers` next to `objectPath` (the ORIGINAL's Storage path,
 * derived from `artwork.url`). Every upload mints a fresh download token, so a
 * repair must not touch a tier that already exists: doing so would invalidate
 * URLs other clients have already resolved for it. Resolves to the download
 * URLs of the tiers written. Callers invalidate exactly those paths.
 */
export const uploadArtworkRenditionTiers = async <T extends RenditionTier>(
  source: Blob,
  objectPath: string,
  tiers: readonly T[],
  customMetadata: Record<string, string>
): Promise<Record<T, string>> => {
  const blobs = await createRenditionBlobsForTiers(source, tiers)
  const storage = Firestore.getInstance()
  const urls = await Promise.all(
    map(tiers, (tier) => {
      const metadata: UploadMetadata = {
        contentType: RENDITION_CONTENT_TYPE,
        cacheControl: "public, max-age=31536000, immutable",
        customMetadata: { ...customMetadata, renditionTier: tier },
      }
      return storage.uploadBlob(
        blobs[tier],
        getRenditionPath(objectPath, tier),
        metadata
      )
    })
  )
  return byTier(tiers, urls)
}

/** Writes both renditions — the fresh-upload path (`ArtworkUploadModal`). */
export const uploadArtworkRenditions = (
  source: Blob,
  objectPath: string,
  customMetadata: Record<string, string>
): Promise<RenditionUrls> =>
  uploadArtworkRenditionTiers(source, objectPath, ALL_TIERS, customMetadata)
